import {
  createContext,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { readLocalStorage, writeLocalStorage } from '../localStorageGuard.js';
import { type Language, setFormatLocale } from '../format.js';
import { type Dictionary, en, fr } from './dictionary.js';

export type { Language };

const STORAGE_KEY = 'room-mates.language';
const DICTIONARIES: Record<Language, Dictionary> = { en, fr };

function isLanguage(value: string | null): value is Language {
  return value === 'en' || value === 'fr';
}

/** No saved choice yet: a French browser gets French, everyone else gets English. */
function detectLanguage(): Language {
  const stored = readLocalStorage(STORAGE_KEY);
  if (isLanguage(stored)) {
    return stored;
  }
  return globalThis.navigator?.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

export interface LanguageContextValue {
  readonly language: Language;
  readonly t: Dictionary;
  setLanguage(language: Language): void;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }): ReactElement {
  const [language, setLanguageState] = useState<Language>(() => {
    const initial = detectLanguage();
    setFormatLocale(initial);
    return initial;
  });

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      t: DICTIONARIES[language],
      setLanguage: (next: Language) => {
        writeLocalStorage(STORAGE_KEY, next);
        setFormatLocale(next);
        setLanguageState(next);
      },
    }),
    [language],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
